import { Component } from "react";
import { Breadcrumbs, ErrorBoundary, Page, PageInner, PageSection, PageHeader, LoadingSpinner } from "@app/shared";
import { RoleRestricted, Roles, UserContext, UserContextProps, withUserContext } from "@app/user";
import ServiceLocation from "./ServiceLocation";
import Practitioner from "./Practitioner";
import PractitionerAtLocation from "./PractitionerAtLocation";
import OutreachService from "../outreachService";
import { NetworkOutreachEmbeddedModel } from "../models";

type OutreachViews = "Service Location" | "Practitioner" | "Practitioner At Location";
interface NetworkOutreachState {
  activeView: string;
  currentQuarter: string;
  isLoading: boolean;
  isReportValid: boolean;
  reportSettings: NetworkOutreachEmbeddedModel;
}

type OutreachProps = UserContextProps;

class NetworkOutreachPage extends Component<OutreachProps, NetworkOutreachState> {
  private readonly exportService = new OutreachService();

  constructor(props: OutreachProps) {
    super(props);

    this.state = {
      activeView: "Service Location",
      currentQuarter: null,
      isLoading: true,
      isReportValid: false,
      reportSettings: null
    };
  }

  public async componentDidMount() {
    let isValid = false;
    let settings: NetworkOutreachEmbeddedModel = null;

    try {
      settings = await this.exportService.getNetworkOutreachConfiguration();
      isValid = this.validateSettings(settings);
    } catch (err) {
      console.error(err);
    }

    this.setState((prevState) => ({
      ...prevState,
      isLoading: false,
      isReportValid: isValid,
      reportSettings: settings
    }));
  }

  private readonly outreachDetailViews = [
    "Service Location",
    "Practitioner",
    "Practitioner At Location"
  ];

  public render() {
    return (
      <UserContext.Consumer>
        {(user) => (
          <RoleRestricted allowedRoles={[Roles.NETWORK_OUTREACH_USER]}>
            <ErrorBoundary user={user}>
              <Page documentTitle="Network Outreach">
                <Breadcrumbs items={[{ text: "Network Outreach" }]} />
                <PageInner>
                  <PageHeader title="Network Outreach" disableHR={true} />
                  <PageSection>
                    <div className="row">
                      <div className="col-12">
                        <div className="tabs-style-2">
                          <ul className="nav nav-tabs">
                            {this.outreachDetailViews.map((key) => (
                              <li className="nav-item" key={key}>
                                <a className={`nav-link${this.state.activeView === key ? " active" : ""}`} role="tab" onClick={() => this.setActiveView(key as OutreachViews)}>{key}</a>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="row mt-3">
                          {this.state.isLoading ?
                            (
                              <div className="row">
                                <div className="col-12">
                                  <div className="text-center">
                                    <LoadingSpinner />
                                  </div>
                                </div>
                              </div>
                            )
                            : this.getActiveView()}
                        </div>
                      </div>
                    </div>
                  </PageSection>
                </PageInner>
              </Page>
            </ErrorBoundary>
          </RoleRestricted>
        )
        }
      </UserContext.Consumer >
    );
  }

  private setActiveView(key: OutreachViews) {
    this.setState((prevState) => ({
      ...prevState,
      activeView: key
    }));
  }

  private getActiveView() {
    switch (this.state.activeView) {
      case "Service Location":
        return (
          <div className="col-12">
            <PageSection>
              {this.state.isReportValid ? (
                <ServiceLocation {...this.state.reportSettings} name="Service Location" pageName={this.state.reportSettings.serviceLocationPageName} setCurrentQuarter={this.setCurrentQuarter} currentQuarter={this.state.currentQuarter} selectorId={this.state.reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      case "Practitioner":
        return (
          <div className="col-12">
            <PageSection>
              {this.state.isReportValid ? (
                <Practitioner {...this.state.reportSettings} name="Practitioner" pageName={this.state.reportSettings.practitionerPageName} setCurrentQuarter={this.setCurrentQuarter} currentQuarter={this.state.currentQuarter} selectorId={this.state.reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      case "Practitioner At Location":
        return (
          <div className="col-12">
            <PageSection>
              {this.state.isReportValid ? (
                <PractitionerAtLocation {...this.state.reportSettings} name="Practitioner At Location" pageName={this.state.reportSettings.practitionerAtLocationPageName} setCurrentQuarter={this.setCurrentQuarter} currentQuarter={this.state.currentQuarter} selectorId={this.state.reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      default:
        return (
          <div className="col-12">
            <PageSection>
              <h3> Unable to display report at this time.</h3>
            </PageSection>
          </div>
        );
    }
  }

  private validateSettings(settings: NetworkOutreachEmbeddedModel) {
    if (settings == null) {
      console.error("Report Configuration Settings returned null.");
      return false;
    }

    let isValid = true;

    if (settings.reportId == null || settings.reportId.length == 0) {
      console.error("Report id settings did not return a value.");
      isValid = false;
    }

    if (settings.token == null || settings.token.length == 0) {
      console.error("Report token settings did not return a value.");
      isValid = false;
    }

    if (settings.url == null || settings.url.length == 0) {
      console.error("Report url settings did not return a value.");
      isValid = false;
    }

    if (settings.clientId == null || settings.clientId.length == 0) {
      console.error("Report client id settings did not return a value.");
      isValid = false;
    }

    if (!settings.minimumQuarter) {
      console.error("Report client minimum quarter did not return a value.");
      isValid = false;
    }

    return isValid;
  }

  public setCurrentQuarter = (quarter: string) => {
    this.setState((prevState) => ({
      ...prevState,
      currentQuarter: quarter
    }));
  }
}

const WrappedPageDataProps = withUserContext(NetworkOutreachPage);

export {
  WrappedPageDataProps as NetworkOutreachPage,
};